/**
 * ElevateEntitlementsAdmin - S5-07 entitlement remainder.
 *
 * Two things the airlines screen's single toggle could not carry:
 *
 *   * The VALIDITY WINDOW. MOD-01 makes the entitlement record — module,
 *     enabled state, validity period — the authority for module activation,
 *     and a toggle with no window fields could only ever set half of it. Worse,
 *     set_module_entitlement upserts every column, so a toggle that omitted the
 *     window arguments silently cleared a stored window on the next flip.
 *   * The HISTORY. Every entitlement change has been audited since migration
 *     238, and until migration 264 nothing could read it: the audit_logs
 *     policies admit an org admin to their own organisation's rows only, and
 *     the service-role toggles carry a null actor no policy matches.
 *
 * The live/not-live verdict on every row is public.has_module's own answer,
 * carried through module_entitlement_overview. This screen does not recompute
 * the window rule.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, History, RefreshCw, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAirline } from '../../contexts/AirlineContext';
import { GuardRefusal } from '../../components/admin/elevate/ContentBadges';
import {
  describeAuditChange,
  describeWindow,
  sortEstate,
  type ModuleEntitlementRow,
} from '../../lib/elevateEntitlements';
import {
  fetchEntitlementAudit,
  fetchEntitlementOverview,
  setModuleEntitlement,
} from '../../lib/elevateEntitlementsApi';

/** `<input type="datetime-local">` wants 'YYYY-MM-DDTHH:mm' and no zone. */
function toLocalInput(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function ElevateEntitlementsAdmin() {
  const { isSuperAdmin } = useAirline();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ModuleEntitlementRow | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: ['elevate-entitlements', 'overview'],
    queryFn: () => fetchEntitlementOverview('elevate'),
    enabled: isSuperAdmin,
  });

  const audit = useQuery({
    queryKey: ['elevate-entitlements', 'audit'],
    queryFn: () => fetchEntitlementAudit({ module: 'elevate' }),
    enabled: isSuperAdmin,
  });

  const save = useMutation({
    mutationFn: setModuleEntitlement,
    onMutate: () => setRefusal(null),
    onSuccess: () => {
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ['elevate-entitlements'] });
      queryClient.invalidateQueries({ queryKey: ['module-entitlements', 'elevate'] });
    },
    onError: (error) => setRefusal((error as Error).message),
  });

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You need super admin permissions to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Elevate Entitlement</h1>
          <p className="mt-1 text-sm text-gray-600">
            Per-tenant module activation and its validity window (MOD-01, MOD-03), with the audited
            history of every change (MOD-05).
          </p>
        </div>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['elevate-entitlements'] })}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {refusal && <GuardRefusal message={refusal} />}
      {overview.error && <GuardRefusal message={(overview.error as Error).message} />}

      {/* The estate */}
      <section className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-x-auto">
        <header className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Tenants</h2>
          <p className="text-xs text-gray-500">
            Live is <code>public.has_module</code>&apos;s own answer — the function every Elevate row-level
            policy goes through — not a second calculation in the browser.
          </p>
        </header>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Enabled</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Validity window</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Live now</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last change</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortEstate(overview.data ?? []).map((row) => (
              <tr key={row.tenant_id} className="hover:bg-gray-50">
                <td className="px-3 py-2 text-xs text-gray-900">
                  <span className="font-medium">{row.tenant_name}</span>
                  <span className="block text-gray-500 font-mono">{row.tenant_code}</span>
                </td>
                <td className="px-2 py-2 text-xs">
                  {row.has_record ? (
                    row.enabled ? (
                      <span className="text-emerald-700">enabled</span>
                    ) : (
                      <span className="text-gray-500">disabled</span>
                    )
                  ) : (
                    <span className="text-gray-400">no record</span>
                  )}
                </td>
                <td className="px-2 py-2 text-xs text-gray-700">
                  {describeWindow(row.valid_from, row.valid_until) ?? (
                    <span className="text-gray-400">unbounded</span>
                  )}
                </td>
                <td className="px-2 py-2 text-xs">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded border ${
                      row.is_live
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                        : 'bg-gray-100 text-gray-700 border-gray-300'
                    }`}
                  >
                    {row.is_live ? 'live' : row.live_reason}
                  </span>
                </td>
                <td className="px-2 py-2 text-xs text-gray-700">
                  {row.updated_at ? (
                    <>
                      <span className="block">{row.updated_at.slice(0, 19).replace('T', ' ')}</span>
                      <span className="block text-gray-500">
                        {row.updated_by_name ?? row.updated_by_email ?? 'service role'}
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right">
                  <button
                    onClick={() => {
                      setRefusal(null);
                      setEditing(row);
                    }}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-wm-deep-blue text-wm-deep-blue hover:bg-ice"
                  >
                    <CalendarClock className="w-3 h-3" />
                    Entitlement and window
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* The audited history */}
      <section className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-x-auto">
        <header className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <History className="w-4 h-4 text-gray-500" />
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Entitlement history</h2>
            <p className="text-xs text-gray-500">
              Every change since migration 238, including the ones a service role made (no actor
              recorded).
            </p>
          </div>
        </header>
        {audit.error && <GuardRefusal message={(audit.error as Error).message} />}
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">When</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Who</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Change</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {(audit.data ?? []).map((row) => (
              <tr key={row.event_id} className="hover:bg-gray-50">
                <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">
                  {row.occurred_at.slice(0, 19).replace('T', ' ')}
                </td>
                <td className="px-2 py-2 text-xs text-gray-900">{row.tenant_name ?? row.tenant_id}</td>
                <td className="px-2 py-2 text-xs text-gray-700">
                  {row.actor_name ?? row.actor_email ?? (
                    <span className="text-gray-400">service role</span>
                  )}
                </td>
                <td className="px-2 py-2 text-xs font-mono text-gray-800">{describeAuditChange(row)}</td>
              </tr>
            ))}
            {(audit.data ?? []).length === 0 && !audit.isLoading && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-500">
                  No entitlement change has been recorded.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {editing && (
        <EntitlementWindowModal
          row={editing}
          isSaving={save.isPending}
          onClose={() => setEditing(null)}
          onSave={(values) => save.mutate({ tenantId: editing.tenant_id, ...values })}
        />
      )}
    </div>
  );
}

// =====================================================================
// The window editor
// =====================================================================

function EntitlementWindowModal({
  row,
  isSaving,
  onClose,
  onSave,
}: {
  row: ModuleEntitlementRow;
  isSaving: boolean;
  onClose: () => void;
  onSave: (values: { enabled: boolean; validFrom: string | null; validUntil: string | null }) => void;
}) {
  const [enabled, setEnabled] = useState(row.enabled);
  const [from, setFrom] = useState(toLocalInput(row.valid_from));
  const [until, setUntil] = useState(toLocalInput(row.valid_until));

  const invalid = Boolean(from && until && new Date(from) >= new Date(until));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Elevate entitlement — {row.tenant_name}</h3>
          <p className="mt-1 text-xs text-gray-600">
            MOD-01: the record is the authority. Both halves are saved together, so setting a window
            never depends on the order the fields were touched in.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setEnabled(!enabled)}
          className="flex items-center gap-2 text-sm text-gray-800"
        >
          {enabled ? (
            <ToggleRight className="w-6 h-6 text-emerald-600" />
          ) : (
            <ToggleLeft className="w-6 h-6 text-gray-400" />
          )}
          {enabled ? 'Enabled' : 'Disabled'}
        </button>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm text-gray-700">
            Valid from
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
            />
            <span className="block mt-1 text-xs text-gray-500">Empty means no start bound.</span>
          </label>
          <label className="text-sm text-gray-700">
            Valid until
            <input
              type="datetime-local"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="mt-1 w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
            />
            <span className="block mt-1 text-xs text-gray-500">
              Empty means no end bound. The module closes the moment this passes.
            </span>
          </label>
        </div>

        {invalid && (
          <p role="alert" className="text-xs text-red-700">
            Valid from must be earlier than valid until — the database refuses the row otherwise
            (tenant_module_entitlements_validity).
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              onSave({ enabled, validFrom: fromLocalInput(from), validUntil: fromLocalInput(until) })
            }
            disabled={isSaving || invalid}
            className="px-3 py-2 text-sm font-medium text-white bg-wm-deep-blue rounded-md hover:bg-[#1C354A] disabled:opacity-40"
          >
            {isSaving ? 'Saving…' : 'Save entitlement'}
          </button>
        </div>
      </div>
    </div>
  );
}
